import React, { useState, useEffect, useRef } from 'react';
import './UserSearchDropdown.css';

/**
 * UserSearchDropdown - A reusable component for searching and selecting users
 * Features:
 * - Debounced search (waits for user to stop typing)
 * - Dropdown with search results
 * - Displays name and email
 * - Returns selected user ID
 */
const UserSearchDropdown = ({
  onSelect,
  placeholder = "Search by name or email...",
  debounceMs = 500,
  searchFn,
  disabled = false,
  initialValue = ""
}) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if query is too short or if a user is already selected
    if (query.trim().length < 2 || selectedUser) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Set loading state
    setIsLoading(true);
    setError(null);

    // Start new debounce timer
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const data = await searchFn(query.trim());
        setResults(data.results || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, searchFn, debounceMs, selectedUser]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Clear selection if user starts typing again
    if (selectedUser) {
      setSelectedUser(null);
      onSelect(null);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setQuery('');
    setShowDropdown(false);
    setResults([]);
    onSelect(user);
    // Clear the input after a brief moment to allow the selection to register
    setTimeout(() => {
      setSelectedUser(null);
    }, 100);
  };

  const handleClear = () => {
    setQuery('');
    setSelectedUser(null);
    setResults([]);
    setShowDropdown(false);
    setError(null);
    onSelect(null);
    inputRef.current?.focus();
  };

  return (
    <div className="user-search-dropdown" ref={dropdownRef}>
      <div className="search-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className={`search-input ${selectedUser ? 'has-selection' : ''}`}
          value={query}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            className="clear-button"
            onClick={handleClear}
            disabled={disabled}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
        {isLoading && (
          <div className="search-spinner">
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {error && (
            <div className="search-error">
              {error}
            </div>
          )}

          {!error && results.length === 0 && !isLoading && (
            <div className="no-results">
              No users found matching "{query}"
            </div>
          )}

          {results.length > 0 && (
            <ul className="results-list">
              {results.map((user) => (
                <li
                  key={user.user_id}
                  className="result-item"
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-info">
                    <div className="user-name">{user.display_name}</div>
                    <div className="user-email">{user.email}</div>
                  </div>
                  <div className={`user-badge ${user.user_type}`}>
                    {user.user_type === 'individual' ? '👤' : user.user_type === 'developer' ? '💻' : '🏢'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selectedUser && (
        <input type="hidden" value={selectedUser.user_id} />
      )}
    </div>
  );
};

export default UserSearchDropdown;
